import React, { useState, useEffect, lazy, Suspense, useCallback} from 'react';
import { Badge, IconButton, Menu, MenuItem, Typography, Box, Divider, ListItemText } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { getNotificaciones, marcarNotificacionLeida, marcarTodasLeidas, eliminarNotificacion } from '../http/actions/NotificacionAction';
import { getDocumentoId } from '../http/actions/DocumentoAction';
import * as signalR from "@microsoft/signalr";
import { jwtDecode } from 'jwt-decode';
import DeleteIcon from '@mui/icons-material/Clear';

const LazyDetalleNormativaModal = lazy(() => import('../modal/Normativa/DetalleNormativaModal'));
const LazyDetalleNormativaModalCliente = lazy(() => import('../modal/Normativa/DetalleNormativaClienteModal'));

const NotificacionesMenu = () => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingNormativa, setLoadingNormativa] = useState(false);
  const [connection] = useState(null);
  const [normativa, setNormativa] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [openDetalleNormativaModal, setOpenDetalleNormativaModal] = useState(false);
  const [openDetalleNormativaClienteModal, setOpenDetalleNormativaClienteModal] = useState(false);  
  
  useEffect(() => {
    const cargarNotificaciones = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const decoded = jwtDecode(token);
        setUserRole(decoded.rol);

        const data = await getNotificaciones(token);
        setNotificaciones(data);
      } catch (error) {
        console.error("Error cargando notificaciones:", error);
      } finally {
        setLoading(false);
      }
    };

    cargarNotificaciones();

    // Configurar SignalR
    const baseUrl = process.env.REACT_APP_BACKEND_URL;

    const connection = new signalR.HubConnectionBuilder()
    .withUrl(`${baseUrl}/notificacionHub`, {
        accessTokenFactory: async () => {
            const token = localStorage.getItem("token");
            return token || "";
        },
        withCredentials: true
    })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Information)
    .build();

    connection.start()
    .then(() => {
        console.log('SignalR conectado');
        
        connection.on('RecibirNotificacion', (data) => {
            console.log("Notificación recibida desde SignalR:", data);
            setNotificaciones(prev => [data, ...prev]);
        });
        
      })
    .catch(err => console.error("Error en la conexión:", err));

    return () => {
      if (connection) {
        connection.stop();
      }
    };
  }, [connection]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificacionClick = async (notificacion) => {
    try {
      const token = localStorage.getItem('token');
      await marcarNotificacionLeida(token, notificacion.id);
      
      // Actualizar estado local
      setNotificaciones(prev => 
        prev.map(n => n.id === notificacion.id ? { ...n, leida: true } : n)
      );

      if (notificacion.tipo === 'NuevaNormativa' && notificacion.normativaId) {

        setLoadingNormativa(true);
        try {
          const normativaCompleta = await getDocumentoId(token, notificacion.normativaId);
          setNormativa(normativaCompleta);
          
          // Abrir el modal correspondiente según el rol
          if (userRole === 'Administrador') {
            setOpenDetalleNormativaModal(true);
          } 
          /*else if (userRole === 'Usuario') {
            setOpenDetalleNormativaClienteModal(true);
          }*/
        } catch (error) {
          console.error("Error al cargar la normativa:", error);
        } finally {
          setLoadingNormativa(false);
        }
      }
      
      handleClose();
    } catch (error) {
      console.error("Error al marcar como leída:", error);
    }
  };

  const handleMarcarTodasLeidas = async () => {
    try {
      const token = localStorage.getItem('token');
      await marcarTodasLeidas(token);
      setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
      handleClose();
    } catch (error) {
      console.error("Error al marcar todas como leídas:", error);
    }
  };

  const handleDeleteNotificacion = async (event, notificacionId) => {
    event.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await eliminarNotificacion(token, notificacionId);
      
      setNotificaciones(prev => prev.filter(n => n.id !== notificacionId));
    } catch (error) {
      console.error("Error al eliminar la notificación:", error);
    }
  };

  const handleCloseDetalleNormativaModal = useCallback(() => {
    setOpenDetalleNormativaModal(false);
    setNormativa(null);
  }, []);

  const handleCloseDetalleNormativaClienteModal = useCallback(() => {
    setOpenDetalleNormativaClienteModal(false);
    setNormativa(null);
  }, []);

  const notificacionesNoLeidas = notificaciones.filter(n => !n.leida).length;

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label="notificaciones"
        sx={{ color: 'black', mr: 2 }}
      >
        <Badge badgeContent={notificacionesNoLeidas} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: 320,
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Notificaciones</Typography>
          {notificacionesNoLeidas > 0 && (
            <Typography 
              variant="body2" 
              color="primary" 
              sx={{ cursor: 'pointer' }}
              onClick={handleMarcarTodasLeidas}
            >
              Marcar todas como leídas
            </Typography>
          )}
        </Box>
        <Divider />
        
        {loading ? (
          <MenuItem>
            <Typography>Cargando notificaciones...</Typography>
          </MenuItem>
        ) : notificaciones.length === 0 ? (
          <MenuItem>
            <Typography>No tienes notificaciones</Typography>
          </MenuItem>
        ) : (
          notificaciones.map((notificacion) => (
            <MenuItem 
              key={notificacion.id} 
              onClick={() => handleNotificacionClick(notificacion)}
              sx={{
                backgroundColor: notificacion.leida ? 'inherit' : 'rgba(25, 118, 210, 0.08)',
                display: 'block',
                position: 'relative',
                paddingRight: '50px'
              }}
            >
              <Box sx={{ width: '100%' }}>
                <ListItemText 
                  primary={
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        paddingRight: '8px'
                      }}
                    >
                      {notificacion.mensaje}
                    </Typography>
                  }
                  secondary={new Date(notificacion.fechaCreacion + 'Z').toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                />

                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={(e) => handleDeleteNotificacion(e, notificacion.id)}
                  sx={{ 
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)'
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
      
      {openDetalleNormativaModal && normativa && (
        <Suspense fallback={<div>Cargando...</div>}>
          <LazyDetalleNormativaModal
            open={openDetalleNormativaModal}
            onClose={handleCloseDetalleNormativaModal}
            normativa={normativa}
            isLoading={loadingNormativa}
          />
        </Suspense>
      )}

      {openDetalleNormativaClienteModal && normativa && (
        <Suspense fallback={<div>Cargando...</div>}>
          <LazyDetalleNormativaModalCliente
            open={openDetalleNormativaClienteModal}
            onClose={handleCloseDetalleNormativaClienteModal}
            normativa={normativa}
            isLoading={loadingNormativa}
          />
        </Suspense>
      )}
    </>
  );
};

export default NotificacionesMenu;